/* ==========================================================================
   RW Forms — engine (needs rwf-fields.js; rwf-submit.js adds RWF.submit).
   Declarative markup contract (see sections/rwf-*.liquid):
     .rwf-step[data-step][data-title][data-sub][data-intro]   one wizard step
     [data-show="expr"]           hidden unless expr is true (steps too)
     [data-required-if="expr"]    field becomes required when expr is true
     required / [data-required]   always required while visible
     [data-validate]              email | phone | zip | url
     [data-rwf-choice] / [data-rwf-ms]   chip/card groups, multi-select
     [data-rwf-text="key"]        live text filled from answers / cfg.vars
     [data-rwf-review]            review cards for all earlier steps
   expr: "name", "!name", "name=a|b", "name!=a", "@rule", joined by && / ||.
   Hidden fields never validate and are stripped from the submission.
   ========================================================================== */
(function () {
  'use strict';
  var RWF = (window.RWF = window.RWF || {});
  var FIELD_SEL = 'input[name], select[name], textarea[name], [data-rwf-choice], [data-rwf-ms]';

  function truthy(x) { return Array.isArray(x) ? x.length > 0 : !(x == null || x === '' || x === false); }
  RWF.truthy = truthy;

  function Form(root, cfg) {
    this.root = root;
    this.cfg = cfg || {};
    this.id = this.cfg.id || root.getAttribute('data-rwf') || 'form';
    this.v = {};
    this.files = {};
    this.fileErr = {};
    this.tried = {};
    this.touched = {};
    this.idx = 0;
    this.steps = Array.prototype.slice.call(root.querySelectorAll('.rwf-step[data-step]'));
    this.scan();
    this.bind();
    this.restore();
    if (this.cfg.init) this.cfg.init(this);
    var done = RWF.pendingSuccess && RWF.pendingSuccess(this);
    if (done) this.showSuccess(done.v, done.vars);
    else this.go(Math.min(this.idx, this.included().length - 1), true);
  }

  Form.prototype.$ = function (sel) { return this.root.querySelector(sel); };
  Form.prototype.$$ = function (sel) { return Array.prototype.slice.call(this.root.querySelectorAll(sel)); };

  /* ---------- Field registry ---------- */
  Form.prototype.scan = function () {
    var self = this;
    this.fields = {};
    this.order = [];
    this.$$(FIELD_SEL).forEach(function (el) {
      if (el.closest('.rwf-hidden-form') || (el.closest('[data-rwf-ms]') && !el.hasAttribute('data-rwf-ms'))) return;
      var box = el.hasAttribute('data-rwf-choice') || el.hasAttribute('data-rwf-ms');
      var name = box ? el.getAttribute(el.hasAttribute('data-rwf-choice') ? 'data-rwf-choice' : 'data-rwf-ms') : el.name;
      if (self.fields[name]) { self.fields[name].els.push(el); return; }
      var kind = el.hasAttribute('data-rwf-choice') ? 'choice' : el.hasAttribute('data-rwf-ms') ? 'ms'
        : el.tagName === 'INPUT' ? (el.type === 'checkbox' || el.type === 'radio' || el.type === 'file' ? el.type : 'text')
        : el.tagName.toLowerCase();
      var f = { name: name, kind: kind, el: el, els: [el], step: el.closest('.rwf-step') };
      self.fields[name] = f;
      self.order.push(f);
      if (kind === 'choice') RWF.wireChoice(el, function (val, multi) { self.pick(name, val, multi); });
      if (kind === 'ms') RWF.wireMulti(el, function (val, on) { self.toggle(name, val, on); });
      if (el.hasAttribute('data-today')) { el.value = RWF.today(); self.v[name] = el.value; }
      if (kind === 'select' && el.value !== '') { el.setAttribute('data-default', el.value); self.v[name] = el.value; }
      if (kind === 'checkbox' && el.checked) { el.setAttribute('data-default', 'true'); self.v[name] = true; }
    });
  };

  Form.prototype.labelOf = function (f) {
    var own = f.el.getAttribute('data-label');
    if (own) return own;
    var wrap = f.el.closest('.rwf-f');
    var l = wrap && wrap.querySelector('.rwf-l');
    if (!l) return f.name;
    var c = l.cloneNode(true);
    c.querySelectorAll('b, span:not([data-rwf-addr]):not([data-rwf-text])').forEach(function (n) { n.remove(); });
    return c.textContent.replace(/\s+/g, ' ').trim();
  };

  Form.prototype.active = function (f) {
    if (f.el.closest('[hidden]') && !f.el.closest('[data-rwf-flow][hidden]')) return false;
    return !f.step || !f.step.hidden;
  };

  Form.prototype.isRequired = function (f) {
    var el = f.el;
    if (el.hasAttribute('required') || el.hasAttribute('data-required')) return true;
    var expr = el.getAttribute('data-required-if');
    return !!(expr && this.test(expr));
  };

  /* ---------- Expressions ---------- */
  Form.prototype.test = function (expr) {
    var self = this;
    return expr.split('||').some(function (or) {
      return or.split('&&').every(function (t) { return self.term(t.trim()); });
    });
  };

  Form.prototype.term = function (t) {
    var v = this.v;
    if (t.charAt(0) === '@') {
      var fn = this.cfg.rules && this.cfg.rules[t.slice(1)];
      return fn ? !!fn(v, this) : false;
    }
    var m = t.match(/^([\w.-]+)\s*(!=|=)\s*(.*)$/);
    if (!m) return t.charAt(0) === '!' ? !truthy(v[t.slice(1)]) : truthy(v[t]);
    var val = v[m[1]];
    var opts = m[3].split('|');
    var hit = Array.isArray(val)
      ? opts.some(function (o) { return val.indexOf(o) > -1; })
      : opts.indexOf(val == null ? '' : String(val)) > -1;
    return m[2] === '=' ? hit : !hit;
  };

  /* ---------- Events ---------- */
  Form.prototype.bind = function () {
    var self = this;
    this.root.addEventListener('input', function (e) {
      var el = e.target;
      if (!el.name || !self.fields[el.name] || el.closest('.rwf-hidden-form')) return;
      // Phone fields: strip anything that isn't a digit or a formatting
      // character (+, space, parens, dash) as the visitor types, so letters
      // can't be entered at all rather than being silently stripped later.
      if (el.type === 'tel') {
        var cleaned = el.value.replace(/[^0-9+()\- ]/g, '');
        if (cleaned !== el.value) {
          var pos = el.selectionStart - (el.value.length - cleaned.length);
          el.value = cleaned;
          if (el.setSelectionRange) el.setSelectionRange(pos, pos);
        }
      }
      if (el.type === 'text' || el.type === 'email' || el.type === 'tel' || el.type === 'url' ||
          el.type === 'number' || el.type === 'date' || el.tagName === 'TEXTAREA') {
        self.v[el.name] = el.value;
        if (el.hasAttribute('data-counter')) RWF.paintCounter(el);
        self.changed(el.name, true);
      }
    });
    this.root.addEventListener('change', function (e) {
      var el = e.target;
      var f = el.name && self.fields[el.name];
      if (!f || el.closest('.rwf-hidden-form')) return;
      if (el.type === 'tel' || el.getAttribute('data-mask') === 'phone') el.value = RWF.fmtPhone(el.value);
      if (f.kind === 'checkbox') self.v[el.name] = el.checked;
      else if (f.kind === 'radio') { if (el.checked) self.v[el.name] = el.value; }
      else if (f.kind === 'file') {
        self.fileErr[el.name] = RWF.checkFiles(el);
        self.files[el.name] = self.fileErr[el.name] ? [] : Array.prototype.slice.call(el.files || []);
        self.v[el.name] = self.files[el.name].map(function (x) { return x.name; }).join(', ');
        RWF.paintFiles(el);
      } else self.v[el.name] = el.value;
      self.touched[el.name] = true;
      self.changed(el.name);
    });
    this.$$('[data-rwf-back]').forEach(function (b) { b.addEventListener('click', function () { self.go(self.idx - 1); }); });
    this.$$('[data-rwf-next]').forEach(function (b) { b.addEventListener('click', function () { self.next(); }); });
    this.$$('[data-rwf-reset]').forEach(function (b) { b.addEventListener('click', function () { self.reset(); }); });
    this.root.addEventListener('click', function (e) {
      var rm = e.target.closest('.rwf-file__remove');
      if (rm) {
        var list = rm.closest('.rwf-file__list');
        var input = list && list.parentNode.querySelector('input[type="file"]');
        if (input) RWF.removeFile(input, parseInt(rm.getAttribute('data-idx'), 10));
        return;
      }
      var g = e.target.closest('[data-rwf-goto]');
      if (g) self.go(self.included().indexOf(self.$('.rwf-step[data-step="' + g.getAttribute('data-rwf-goto') + '"]')));
    });
  };

  Form.prototype.pick = function (name, val, multi) {
    if (multi) {
      var arr = (this.v[name] || []).slice();
      var i = arr.indexOf(val);
      if (i > -1) arr.splice(i, 1); else arr.push(val);
      this.v[name] = arr;
    } else {
      var box = this.fields[name].el;
      this.v[name] = box.hasAttribute('data-toggle') && this.v[name] === val ? '' : val;
    }
    this.touched[name] = true;
    this.changed(name);
  };

  Form.prototype.toggle = function (name, val, on) {
    var arr = (this.v[name] || []).filter(function (x) { return x !== val; });
    if (on) arr.push(val);
    this.v[name] = arr;
    this.touched[name] = true;
    this.changed(name);
  };

  /* Programmatic setter used by form modules (keeps DOM + state in sync). */
  Form.prototype.set = function (name, val, silent) {
    this.v[name] = val;
    var f = this.fields[name];
    if (f) this.setDom(f, val);
    if (!silent) this.changed(name);
  };

  Form.prototype.setDom = function (f, val) {
    if (f.kind === 'choice' || f.kind === 'ms' || f.kind === 'file') return;
    if (f.kind === 'checkbox') { f.el.checked = !!val; return; }
    if (f.kind === 'radio') { f.els.forEach(function (r) { r.checked = r.value === val; }); return; }
    f.el.value = val == null ? '' : val;
    if (f.el.hasAttribute('data-counter')) RWF.paintCounter(f.el);
  };

  Form.prototype.changed = function (name, typing) {
    var self = this;
    var f = this.fields[name];
    if (f && f.el.hasAttribute('data-rwf-country')) {
      this.$$('[data-rwf-addr="region"][data-rwf-country-of="' + name + '"]').forEach(function (r) {
        delete self.v[r.name];
        r.value = '';
      });
    }
    if (this.cfg.onChange) this.cfg.onChange(name, this.v, this);
    this.save();
    this.refresh(typing);
  };

  /* ---------- Rendering ---------- */
  Form.prototype.refresh = function (typing) {
    var self = this;
    this.$$('[data-show]').forEach(function (el) { el.hidden = !self.test(el.getAttribute('data-show')); });
    this.$$('[data-rwf-country]').forEach(function (c) { RWF.paintAddress(self.root, c.name, self.v[c.name] || c.value || 'US'); });
    this.order.forEach(function (f) {
      if (f.kind === 'select' && f.el.value !== (self.v[f.name] || '')) f.el.value = self.v[f.name] || '';
      if (f.kind === 'choice') RWF.paintChoice(f.el, self.v[f.name]);
      if (f.kind === 'ms') RWF.paintMulti(f.el, self.v[f.name]);
    });
    if (this.cfg.render) this.cfg.render(this.v, this);
    this.paintText();
    this.paintChrome();
    if (!typing && this.cur() && this.cur().querySelector('[data-rwf-review]')) this.buildReview();
    this.paintErrors();
  };

  Form.prototype.vars = function () {
    var self = this;
    var out = {};
    this.order.forEach(function (f) { out[f.name] = self.display(f); });
    return Object.assign(out, this.cfg.vars ? this.cfg.vars(this.v, this) : {});
  };

  Form.prototype.paintText = function (vars) {
    vars = vars || this.vars();
    this.$$('[data-rwf-text]').forEach(function (el) {
      var k = el.getAttribute('data-rwf-text');
      el.textContent = vars[k] != null && vars[k] !== '' ? vars[k] : (el.getAttribute('data-fallback') || '');
    });
  };

  Form.prototype.included = function () { return this.steps.filter(function (s) { return !s.hidden; }); };
  Form.prototype.cur = function () { return this.included()[this.idx]; };

  Form.prototype.paintChrome = function () {
    var self = this;
    var inc = this.included();
    var cur = inc[this.idx];
    this.steps.forEach(function (s) { s.classList.toggle('is-active', s === cur); s.style.display = s === cur ? '' : 'none'; });
    var nav = this.$('[data-rwf-steps]');
    if (nav) {
      nav.innerHTML = inc.map(function (s, i) {
        var st = i < self.idx ? 'done' : i === self.idx ? 'current' : 'todo';
        var dot = st === 'done' ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7"/></svg>' : String(i + 1);
        return '<button type="button" class="rwf-st" data-st="' + st + '" data-i="' + i + '"' + (st === 'current' ? ' aria-current="step"' : '') +
          (st === 'todo' ? ' disabled' : '') + '><span class="rwf-dot">' + dot + '</span><span class="rwf-st__txt"><span class="rwf-st__n">' +
          RWF.esc(s.getAttribute('data-title')) + '</span><span class="rwf-st__s">' + RWF.esc(s.getAttribute('data-sub') || '') + '</span></span></button>';
      }).join('');
      nav.onclick = function (e) {
        var b = e.target.closest('.rwf-st[data-st="done"]');
        if (b) self.go(parseInt(b.getAttribute('data-i'), 10));
      };
    }
    if (!cur) return;
    var set = function (sel, txt) { var el = self.$(sel); if (el) el.textContent = txt; };
    set('[data-rwf-count]', 'Step ' + (this.idx + 1) + ' of ' + inc.length);
    set('[data-rwf-title]', cur.getAttribute('data-title'));
    set('[data-rwf-intro]', cur.getAttribute('data-intro') || '');
    var bar = this.$('[data-rwf-bar]');
    if (bar) bar.style.width = Math.round(((this.idx + 1) / inc.length) * 100) + '%';
    var last = this.idx === inc.length - 1;
    this.$$('[data-rwf-back]').forEach(function (b) { b.hidden = self.idx === 0; });
    this.$$('[data-rwf-next]').forEach(function (b) {
      if (!b.disabled) b.textContent = last ? (self.root.getAttribute('data-submit-label') || 'Submit') : 'Continue';
    });
  };

  /* ---------- Validation ---------- */
  Form.prototype.errorsFor = function (step) {
    var self = this;
    var errs = [];
    var inline = {};
    var bad = {};
    this.order.forEach(function (f) {
      if (f.step !== step || !self.active(f)) return;
      var val = self.v[f.name];
      var label = f.el.getAttribute('data-err') || self.labelOf(f);
      if (f.kind === 'file' && self.fileErr[f.name]) { errs.push(label); inline[f.name] = self.fileErr[f.name]; bad[f.name] = 1; return; }
      if (!truthy(val) || (typeof val === 'string' && !val.trim())) {
        if (self.isRequired(f)) { errs.push(label); bad[f.name] = 1; }
        return;
      }
      var msg = self.check(f, String(val).trim(), label);
      var custom = self.cfg.validate && self.cfg.validate[f.name] && self.cfg.validate[f.name](val, self.v, self);
      if (custom) msg = typeof custom === 'string' ? { pill: custom } : custom;
      if (msg) { errs.push(msg.pill); bad[f.name] = 1; if (msg.inline) inline[f.name] = msg.inline; }
    });
    if (this.cfg.stepErrors) errs = errs.concat(this.cfg.stepErrors(step.getAttribute('data-step'), this.v, this) || []);
    return { list: errs, inline: inline, bad: bad };
  };

  Form.prototype.check = function (f, val, label) {
    var type = f.el.getAttribute('data-validate');
    if (type === 'email' && !RWF.EMAIL.test(val)) return { pill: label + ' (check format)', inline: RWF.EMAIL_MSG };
    if (type === 'phone' && !RWF.phoneOk(val)) return { pill: label + ' (check number)', inline: RWF.PHONE_MSG };
    if (type === 'url' && !RWF.URL.test(val)) return { pill: label + ' (check format)', inline: 'Enter a web address like www.example.com' };
    if (type === 'zip') {
      var L = RWF.loc(this.v[f.el.getAttribute('data-rwf-country-of')] || 'US');
      if (L.zipRe && !L.zipRe.test(val)) return { pill: label + ' (check format)' };
    }
    if (type === 'tax') {
      var Lt = RWF.loc(this.v[f.el.getAttribute('data-rwf-country-of')] || 'US');
      if (Lt.taxRe && !Lt.taxRe.test(val)) return { pill: label + ' (format ' + Lt.taxPh + ')' };
    }
    if (type === 'percent') {
      if (!/^\d{1,3}$/.test(val) || parseInt(val, 10) > 100) return { pill: label + ' (0–100)' };
    }
    var pat = f.el.getAttribute('data-pattern');
    if (pat && !new RegExp('^(?:' + pat + ')$').test(val)) return { pill: f.el.getAttribute('data-err-pattern') || label + ' (check format)' };
    if (f.el.type === 'number') {
      var n = parseFloat(val);
      var min = f.el.getAttribute('min');
      var max = f.el.getAttribute('max');
      if (isNaN(n) || (min !== null && n < parseFloat(min)) || (max !== null && n > parseFloat(max))) return { pill: label + ' (check number)' };
    }
    return null;
  };

  Form.prototype.paintErrors = function () {
    var self = this;
    var step = this.cur();
    if (!step) return;
    var id = step.getAttribute('data-step');
    var r = this.errorsFor(step);
    var shown = !!this.tried[id];
    var box = this.$('[data-rwf-errors]');
    if (box) {
      box.hidden = !(shown && r.list.length);
      var list = box.querySelector('.rwf-errors__list');
      if (list) list.innerHTML = r.list.map(function (e) { return '<span>' + RWF.esc(e) + '</span>'; }).join('');
    }
    this.order.forEach(function (f) {
      if (f.step !== step) return;
      var show = shown || self.touched[f.name];
      var target = f.kind === 'choice' || f.kind === 'ms' ? f.el : f.els[0];
      if (f.kind === 'choice' || f.kind === 'ms') target.classList.toggle('is-invalid', !!(shown && r.bad[f.name]));
      else target.setAttribute('aria-invalid', shown && r.bad[f.name] ? 'true' : 'false');
      var holder = target.closest('.rwf-f') || target.parentNode;
      var p = holder.querySelector('.rwf-err[data-for="' + f.name + '"]');
      var msg = show && r.inline[f.name];
      if (msg && !p) {
        p = document.createElement('p');
        p.className = 'rwf-err';
        p.setAttribute('data-for', f.name);
        holder.appendChild(p);
      }
      if (p) { p.textContent = msg || ''; p.hidden = !msg; }
    });
    return r;
  };

  /* ---------- Navigation ---------- */
  Form.prototype.go = function (i, initial) {
    var inc = this.included();
    this.idx = Math.max(0, Math.min(i, inc.length - 1));
    this.save();
    this.refresh();
    if (!initial) {
      var top = this.root.getBoundingClientRect().top + window.pageYOffset - 80;
      if (window.pageYOffset > top) window.scrollTo({ top: top, behavior: 'smooth' });
    }
  };

  Form.prototype.next = function () {
    var step = this.cur();
    var r = this.errorsFor(step);
    if (r.list.length) {
      this.tried[step.getAttribute('data-step')] = true;
      this.paintErrors();
      var box = this.$('[data-rwf-errors]');
      if (box) box.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (this.idx < this.included().length - 1) { this.go(this.idx + 1); return; }
    var blocked = this.included().filter(function (s) { return this.errorsFor(s).list.length; }, this)[0];
    if (blocked) {
      this.tried[blocked.getAttribute('data-step')] = true;
      this.go(this.included().indexOf(blocked));
      return;
    }
    if (RWF.submit) RWF.submit(this);
  };

  /* ---------- Draft persistence ---------- */
  Form.prototype.key = function () { return 'rwf:' + this.id; };
  Form.prototype.save = function () {
    try { localStorage.setItem(this.key(), JSON.stringify({ v: this.v, idx: this.idx })); } catch (e) { /* storage unavailable */ }
  };
  Form.prototype.restore = function () {
    var self = this;
    var d = null;
    try { d = JSON.parse(localStorage.getItem(this.key()) || 'null'); } catch (e) { d = null; }
    if (!d || !d.v) return;
    this.order.forEach(function (f) {
      if (f.kind === 'file' || f.el.hasAttribute('data-today') || !(f.name in d.v)) return;
      self.v[f.name] = d.v[f.name];
      self.setDom(f, d.v[f.name]);
    });
    this.idx = d.idx || 0;
  };

  /* ---------- Success / reset ---------- */
  Form.prototype.showSuccess = function (v, vars) {
    if (v) this.v = v;
    this.refresh();
    this.paintText(Object.assign(this.vars(), vars || {}));
    var hasFiles = !!(vars && vars.hasFiles);
    this.$$('[data-rwf-files-note]').forEach(function (n) { n.hidden = !hasFiles; });
    var flow = this.$('[data-rwf-flow]');
    var ok = this.$('[data-rwf-success]');
    if (flow) flow.hidden = true;
    if (ok) ok.hidden = false;
    this.$$('.rwf-st').forEach(function (b) { b.setAttribute('data-st', 'done'); b.disabled = true; });
    this.$$('[data-rwf-next]').forEach(function (b) { b.disabled = false; });
    try { localStorage.removeItem(this.key()); } catch (e) { /* ignore */ }
    this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  Form.prototype.reset = function () {
    var self = this;
    try { localStorage.removeItem(this.key()); sessionStorage.removeItem('rwf:done:' + this.id); } catch (e) { /* ignore */ }
    if (/contact_posted/.test(window.location.search)) history.replaceState(null, '', window.location.pathname);
    this.v = {};
    this.files = {};
    this.fileErr = {};
    this.tried = {};
    this.touched = {};
    this.order.forEach(function (f) {
      if (f.kind === 'file') { f.el.value = ''; RWF.paintFiles(f.el); }
      else if (f.el.hasAttribute('data-today')) { f.el.value = RWF.today(); self.v[f.name] = f.el.value; }
      else if (f.kind === 'select' && f.el.hasAttribute('data-default')) { self.v[f.name] = f.el.getAttribute('data-default'); self.setDom(f, self.v[f.name]); }
      else if (f.kind === 'checkbox' && f.el.hasAttribute('data-default')) { self.v[f.name] = true; self.setDom(f, true); }
      else self.setDom(f, '');
    });
    if (this.cfg.onReset) this.cfg.onReset(this);
    var flow = this.$('[data-rwf-flow]');
    var ok = this.$('[data-rwf-success]');
    if (flow) flow.hidden = false;
    if (ok) ok.hidden = true;
    this.go(0);
  };

  RWF.Form = Form;
  RWF.init = function (sel, cfg) {
    var root = typeof sel === 'string' ? document.querySelector(sel) : sel;
    return root ? new Form(root, cfg) : null;
  };
})();
